import { useMutation } from "@apollo/client/react";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFlags } from "launchdarkly-react-client-sdk";
import moment from "moment";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";

import { isUUID } from "./utils";
import {
  useAuth,
  useNoteUsage,
  usePreferences,
  useRecordingJobType,
} from "../../hooks";
import { useTeam } from "../../hooks/use-team";
import { alert } from "../common/Alert";
import Record from "../common/record/Record";
import { CREATE_QUICK_NOTE } from "../notes/edit/api/mutations";

export function DashboardRecorder({ onIsRecordingChanged }) {
  const { setFirstTimeTourDisabled } = usePreferences();
  const [isRecording, setIsRecording] = useState(false);
  const { releaseWebEnableFtueTour } = useFlags();
  const { totalNotesCreated } = useNoteUsage();
  const { control, getValues, reset } = useForm();
  const { userUuid } = useAuth();
  const { currentTeam } = useTeam();
  const location = useLocation();
  const isPimsPal = location.pathname
    .toLowerCase()
    .includes("/pimspal/");

  const { selectedJobType } = useRecordingJobType();
  const [createQuickNote] = useMutation(CREATE_QUICK_NOTE);
  async function createNewNote(
    audioStorageLink,
    storageObjectName,
    jobTypeArg,
    isSyncedRecording = false,
    isNoteFromFileUpload = false,
    recordingAs = userUuid,
    titleArg = null,
  ) {
    const values = getValues();
    let jobType;
    let title;

    // If a job type is passed in, use that (e.g., via unsynced recordings)
    if (jobTypeArg) {
      jobType = jobTypeArg;
    } else if (selectedJobType) {
      jobType = selectedJobType?.value;
    } else {
      // Default to "medical_record"
      jobType = "medical_record";
    }

    // If a title is passed in, use that (e.g., via unsynced recordings)
    if (titleArg !== null) {
      title = titleArg;
    } else {
      title = values.title;
    }
    const isTemplate = isUUID(jobType);

    const noteData = await createQuickNote({
      variables: {
        noteInput: {
          audioStorageLink: audioStorageLink,
          storageObjectName: storageObjectName,
          createdAt: moment().format(),
          lastUpdatedAt: moment().format(),
          title: title,
          teamUuid: currentTeam?.uuid,
          jobType: isTemplate ? "template" : jobType,
          ...(isTemplate && {
            templateUuid: jobType,
          }),
          recordingAs,
        },
      },
    });

    // reset transcript between recordings
    if (!isSyncedRecording && !isNoteFromFileUpload) {
      alert("success", "Audio file uploaded!");
    }

    return noteData?.data?.createQuickNote?.note;
  }

  return (
    <>
      <div
        id="dashboardRecorder"
        className="border border-primary bg-primary rounded-xl shadow-xs flex flex-col relative flex-1 self-stretch"
      >
        <div className="flex flex-row justify-between w-full rounded-xl bg-secondary border-b border-secondary p-4 shadow-xs">
          <h3 className="text-primary">
            Create a Recording
            {releaseWebEnableFtueTour &&
              !isPimsPal &&
              totalNotesCreated !== undefined &&
              totalNotesCreated > 10 && (
                <span
                  onClick={() => {
                    setFirstTimeTourDisabled(false);
                  }}
                  title="Show Me How"
                >
                  <FontAwesomeIcon
                    className="inline-block cursor-help fill-current text-placeholder hover:text-brand ml-2"
                    icon={faQuestionCircle}
                    size="sm"
                  />
                </span>
              )}
          </h3>
        </div>

        <div className="p-4">
          <Record
            isRecording={isRecording}
            setIsRecording={(isRecording) => {
              setIsRecording(isRecording);
              onIsRecordingChanged?.(isRecording);
            }}
            control={control}
            getValues={getValues}
            createNewNote={createNewNote}
            shouldShowTitleInput={true}
            shouldUseSmallSize={true}
            reset={reset}
          />
        </div>
      </div>
    </>
  );
}
